/**
 * ThemeContext — 3-mode theme (system / light / dark).
 *
 * Mode semantics:
 *   - 'system' (default): follows the OS `prefers-color-scheme`. Updates live
 *     when the OS preference flips.
 *   - 'light' / 'dark': explicit user choice, ignores OS.
 *
 * The MODE is what we store in localStorage. The RESOLVED theme is what we
 * write onto <html data-theme="dark"> (or remove for light). Tokens in
 * `trodoColors.js` flip on that attribute.
 *
 * Usage:
 *   import { ThemeProvider, useTheme } from 'shared/theme/ThemeContext';
 *   const { mode, theme, setMode } = useTheme();
 *   setMode('system' | 'light' | 'dark');
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const STORAGE_KEY = 'trodo-theme';
const VALID_MODES = ['system', 'light', 'dark'];

const ThemeContext = createContext({
  mode: 'system',
  theme: 'light',
  setMode: () => {},
  toggleTheme: () => {},
});

function readInitialMode() {
  if (typeof window === 'undefined') return 'system';
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (VALID_MODES.includes(stored)) return stored;
  } catch {
    // localStorage blocked — fall through to default.
  }
  return 'system';
}

function getSystemTheme() {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function resolveTheme(mode, systemTheme) {
  return mode === 'system' ? systemTheme : mode;
}

function applyThemeAttribute(theme) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (theme === 'dark') root.setAttribute('data-theme', 'dark');
  else root.removeAttribute('data-theme');
  // Native controls (range tracks, checkboxes, scrollbars, spinners) don't read
  // our tokens — they follow `color-scheme`, which defaults to the OS. So a user
  // on a dark OS who picks our light theme got black slider tracks on white,
  // and vice-versa. Tell the browser which built-in palette the app is actually
  // painting so its own widgets match.
  root.style.colorScheme = theme === 'dark' ? 'dark' : 'light';
}

export function ThemeProvider({ children }) {
  const [mode, setModeState] = useState(readInitialMode);
  const [systemTheme, setSystemTheme] = useState(getSystemTheme);

  // Listen for OS preference changes so 'system' mode tracks them live.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e) => setSystemTheme(e.matches ? 'dark' : 'light');
    if (mq.addEventListener) mq.addEventListener('change', handler);
    else if (mq.addListener) mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', handler);
      else if (mq.removeListener) mq.removeListener(handler);
    };
  }, []);

  const theme = resolveTheme(mode, systemTheme);

  // Apply resolved theme to <html>.
  useEffect(() => {
    applyThemeAttribute(theme);
  }, [theme]);

  // Persist mode.
  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      // Best-effort.
    }
  }, [mode]);

  const setMode = useCallback((next) => {
    setModeState(VALID_MODES.includes(next) ? next : 'system');
  }, []);

  const toggleTheme = useCallback(() => {
    setModeState((prev) => {
      const current = resolveTheme(prev, getSystemTheme());
      return current === 'dark' ? 'light' : 'dark';
    });
  }, []);

  const value = useMemo(
    () => ({ mode, theme, setMode, toggleTheme }),
    [mode, theme, setMode, toggleTheme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  return useContext(ThemeContext);
}
